/**
 * The menu bar: Nova's mark, the standard menus, and the layout switch.
 *
 * Behaves like a desktop menu bar: click a menu to open it, then hovering the
 * others switches between them; Left/Right move between menus while one is
 * open; Escape closes. Alt+letter is left to the operating system.
 */

import { useState } from "react";
import { useApp } from "../core/app";
import { useRegistry } from "../core/registry";
import { isDesktop } from "../platform";
import { MoreButton, PopupMenu } from "./PopupMenu";
import { useMenus } from "./menus";

export function Brand() {
  return (
    <span className="nv-brand">
      <i aria-hidden="true" />
      Nova
    </span>
  );
}

export function LayoutSwitch() {
  const app = useApp();
  const { layouts } = useRegistry();
  if (app.layoutLocked) return null;
  return (
    <div className="nv-seg" role="radiogroup" aria-label="Layout">
      {layouts.map((l) => (
        <button
          key={l.id}
          role="radio"
          aria-checked={app.layout === l.id}
          className={app.layout === l.id ? "on" : ""}
          title={l.description}
          onClick={() => app.setLayout(l.id)}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export function MenuBar({ compactTitle }: { compactTitle?: boolean }) {
  const menus = useMenus();
  const [open, setOpen] = useState<{ index: number; rect: DOMRect } | null>(null);

  const openAt = (index: number) => {
    const el = document.querySelector<HTMLElement>(`[data-nv-menu="${index}"]`);
    if (el) setOpen({ index, rect: el.getBoundingClientRect() });
  };

  return (
    <nav
      className="nv-menubar"
      aria-label="Menu bar"
      onKeyDown={(e) => {
        if (!open) return;
        if (e.key === "ArrowRight") openAt((open.index + 1) % menus.length);
        else if (e.key === "ArrowLeft") openAt((open.index - 1 + menus.length) % menus.length);
      }}
    >
      <Brand />
      <div className="nv-menus" role="menubar">
        {menus.map((m, i) => (
          <button
            key={m.id}
            data-nv-menu={i}
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={open?.index === i}
            className={`nv-menutitle ${open?.index === i ? "on" : ""}`}
            onClick={(e) => setOpen(open?.index === i ? null : { index: i, rect: e.currentTarget.getBoundingClientRect() })}
            onMouseEnter={(e) => open && open.index !== i && setOpen({ index: i, rect: e.currentTarget.getBoundingClientRect() })}
          >
            {m.label}
          </button>
        ))}
      </div>
      <span className="nv-spacer" />
      {!compactTitle && <LayoutSwitch />}
      {open && (
        <PopupMenu
          key={open.index}
          items={menus[open.index].items}
          anchor={{ rect: open.rect }}
          label={menus[open.index].label}
          onClose={() => setOpen(null)}
        />
      )}
    </nav>
  );
}

/**
 * In a browser there is no menu bar: the same menus sit under one ⋯ button,
 * the way web apps do it. The desktop app keeps the standard menu bar.
 */
export const showMenuBar = () => isDesktop();

export function MenusButton() {
  const menus = useMenus();
  return <MoreButton label="Menu" items={() => menus.map((m) => ({ id: m.id, label: m.label, submenu: m.items }))} />;
}

/** The toolbar's ends in a browser: the brand on the left, layouts and ⋯ on the right. */
export function WebBrand() {
  return showMenuBar() ? null : <Brand />;
}

export function WebTrailing() {
  if (showMenuBar()) return null;
  return (
    <>
      <LayoutSwitch />
      <MenusButton />
    </>
  );
}
